import {
  call,
  put,
  takeLatest
} from "redux-saga/effects";
import {
  downloadFile,
  downloadXLSFile
} from "utils/api";
import {
  ToastSuccess,
  ToastError
} from "components/Global/Toasts/Toasty";

import {
  defaultReturn,
  makePayload,
  generateDefaultActions,
  subKeySaga,
  requestSaga,
  fillDictionary
} from "../../utils/helpers";

const initialState = {
  error: "",
  testData: {
    yieldCurveResult: 0
  },
  prepaymentPenalty: {},
  loanProceeds: {},
  refiBreakEvenResults: {}
};

export const GET_TEST_DATA = generateDefaultActions("GET_TEST_DATA");
export const GET_YIELD_TEST_DATA = generateDefaultActions("GET_YIELD_TEST_DATA");
export const DOWNLOAD_TEST_DATA = generateDefaultActions("DOWNLOAD_TEST_DATA");
export const GET_AMORT_TEST_DATA = generateDefaultActions("GET_AMORT_TEST_DATA");
export const DOWNLOAD_AMORT_TEST_DATA = generateDefaultActions("DOWNLOAD_AMORT_TEST_DATA");
export const RESET_TEST_DATA = "RESET_TEST_DATA";

// CALCULATE_PREPAYMENT_PENALTY
export const CALCULATE_PREPAYMENT_PENALTY = generateDefaultActions("CALCULATE_PREPAYMENT_PENALTY");
export const calculatePrepaymentPenalty = payload => makePayload(CALCULATE_PREPAYMENT_PENALTY.value, payload);
function* calculatePrepaymentPenaltySaga(payload) {
  const url = "/calc/calculatePrepaymentPenalty";
  yield requestSaga(CALCULATE_PREPAYMENT_PENALTY, url, "POST", payload);
}

// DOWNLOAD_PREPAYMENT_PENALTY_TEST_DATA
export const DOWNLOAD_PREPAYMENT_PENALTY_TEST_DATA = generateDefaultActions("DOWNLOAD_PREPAYMENT_PENALTY_TEST_DATA");
export const downloadPrepaymentPenaltyTestData = payload => makePayload(DOWNLOAD_PREPAYMENT_PENALTY_TEST_DATA.value, payload);
function* downloadPrepaymentPenaltyTestSaga(payload) {
  try {
    yield call(downloadXLSFile, "downloadPrepaymentPenaltyTest", "POST", payload);
  } catch (error) {
    yield put(DOWNLOAD_PREPAYMENT_PENALTY_TEST_DATA.createFailure(error));
  }
}

// DOWNLOAD_CAP_PRICER_TEST_DATA
export const DOWNLOAD_CAP_PRICER_TEST_DATA = generateDefaultActions("DOWNLOAD_CAP_PRICER_TEST_DATA");
export const downloadCapPricerTestData = payload => makePayload(DOWNLOAD_CAP_PRICER_TEST_DATA.value, payload);
function* downloadCapPricerTestDataSaga(payload) {
  try {
    yield call(downloadXLSFile, "downloadCapPricerTest", "POST", payload);
  } catch (error) {
    yield put(DOWNLOAD_CAP_PRICER_TEST_DATA.createFailure(error));
  }
}

export const CALCULATE_REFI_BREAKEVEN = generateDefaultActions("CALCULATE_REFI_BREAKEVEN");
export const calculateRefiBreakEven = payload => makePayload(CALCULATE_REFI_BREAKEVEN.value, payload);
function* calculateRefiBreakEvenSaga(payload) {
  try {
    const url = "/refi/breakeven-harness";
    yield requestSaga(CALCULATE_REFI_BREAKEVEN, url, "POST", payload);
  } catch (error) {
    yield put(CALCULATE_REFI_BREAKEVEN.createFailure(error));
  }
}

/*
 * Available Parms from payload
 * dayOfMonth
 * curve
 *  shockType
 *  page
 *  pageSize
 *  shockValue
 *  baseDate
 *  tenorValue
 *  selectedTab
 *  loanAmount
 *  interestRateType
 *  rate
 *  spread
 *  parallelShiftAmt
 *  startDate
 *  loanTerm
 *  daycount
 *  amortizationType
 *  amortizationRate
 *  amortizationTerm
 *  customMonthlyPrincipalReduction
 *  ioPeriod
 *  payment
 *  principalReduction
 */
export default function testDataReducer(state = initialState, action) {
  const {
    type,
    payload,
    subKey
  } = action;
  switch (type) {
    case GET_TEST_DATA.success:
      return defaultReturn(state, payload, "testData");
    case GET_YIELD_TEST_DATA.success:
      return fillDictionary(state, payload.yieldCurveResult, "testData", "yieldCurveResult");
    case CALCULATE_PREPAYMENT_PENALTY.success:
      ToastSuccess("Calculation Complete");

      return defaultReturn(state, payload, "prepaymentPenalty");
    case CALCULATE_PREPAYMENT_PENALTY.failure:
      ToastError(`Error: Please check your calculation selections (${payload.response.statusText})`);

      return state;
    case GET_AMORT_TEST_DATA.success:
      if (payload.errorMessage !== null || payload.errorMessage !== undefined || payload.errorMessage !== "") {
        ToastError(payload.errorMessage);
      }

      return defaultReturn(state, payload, "testData");

    case CALCULATE_REFI_BREAKEVEN.success:
      return defaultReturn(state, payload, "refiBreakEvenResults");

    case GET_TEST_DATA.failure:
      ToastError(payload.response.data.message);

      return state;
    case GET_YIELD_TEST_DATA.failure:
      ToastError(payload.response.data.message);

      return state;
    case GET_AMORT_TEST_DATA.failure:
      ToastError(payload.response.data.message);

      return state;

    case DOWNLOAD_TEST_DATA:
    case DOWNLOAD_AMORT_TEST_DATA:
    case DOWNLOAD_PREPAYMENT_PENALTY_TEST_DATA:
      return defaultReturn(state, payload, "");
    case DOWNLOAD_CAP_PRICER_TEST_DATA:
      return defaultReturn(state, payload, "");
    case CALCULATE_LOAN_PROCEEDS.success:
      payload.success = true;

      return fillDictionary(state, payload, "loanProceeds", subKey);
    case CALCULATE_LOAN_PROCEEDS.failure:
      payload.success = false;
      ToastError(payload.response.data.message);

      return fillDictionary(state, payload, "loanProceeds", subKey);

    case RESET_TEST_DATA:
      return {
        ...state,
        ...initialState
      };

    default:
      return state;
  }
}

export const getTestData = payload => makePayload(GET_TEST_DATA.value, payload);
export const getYieldTestData = payload => makePayload(GET_YIELD_TEST_DATA.value, payload);
export const getAmortTestData = payload => makePayload(GET_AMORT_TEST_DATA.value, payload);
export const downloadTestData = payload => makePayload(DOWNLOAD_TEST_DATA.value, payload);
export const downloadAmortTestData = payload => makePayload(DOWNLOAD_AMORT_TEST_DATA.value, payload);

function* getTestDataSaga(payload) {
  const urlParams = buildURL(payload, ["type",
    "tenorValue",
    "selectedTab",
    "loanAmount",
    "interestRateType",
    "rate",
    "spread",
    "parallelShiftAmt",
    "startDate",
    "loanTerm",
    "daycount",
    "amortizationType",
    "amortizationRate",
    "amortizationRate",
    "amortizationTerm",
    "customMonthlyPrincipalReduction",
    "ioPeriod",
    "payment",
    "principalReduction"]);
  const url = `/calc/forwardCurve?${urlParams}`;
  yield requestSaga(GET_TEST_DATA, url);
}

function* getYieldTestDataSaga(payload) {
  const urlParams = buildURL(payload, ["type", "curve"]);
  const url = `/calc/${payload.curve}?${urlParams}`;
  yield requestSaga(GET_YIELD_TEST_DATA, url, "GET", null, null, true);
}

function* getAmortTestDataSaga(payload) {
  const url = "/debtValuationCalculator";
  yield requestSaga(GET_AMORT_TEST_DATA, url, "POST", payload);
}

// helpers
function buildURL(payload, excludedKeys) {
  let url = "";
  for (const key in payload) {
    if (!excludedKeys.includes(key) && (payload[key] !== 0 || !payload[key])) {
      if (url != "") {
        url = `${url}&`;
      }
      url = `${url}${key}=${encodeURIComponent(payload[key])}`;
    }
  }

  return url;
}

function* downloadDataSaga(payload) {
  try {
    const urlParams = buildURL(payload, ["type", "tenorValue", "selectedTab", "loanAmount", "interestRateType", "rate", "spread", "parallelShiftAmt", "startDate", "loanTerm", "daycount", "amortizationType", "amortizationRate", "amortizationRate", "amortizationTerm", "customMonthlyPrincipalReduction", "ioPeriod", "payment", "principalReduction"]);
    const url = `downloadCalculatorTest?${urlParams}`;
    yield call(downloadFile, url, "GET");
  } catch (error) {
    yield put(DOWNLOAD_TEST_DATA.createFailure(error));
  }
}

function* downloadAmortizationTestSaga(payload) {
  try {
    const response = yield call(downloadXLSFile, "/downloadAmortizationTest", "POST", payload, "Amortization.xlsx");
    yield put({ type: DOWNLOAD_TEST_DATA, payload: response });
  } catch (error) {
    yield put({ type: DOWNLOAD_TEST_DATA, error });
  }
}

export const CALCULATE_LOAN_PROCEEDS = generateDefaultActions("CALCULATE_LOAN_PROCEEDS");
export const calculateLoanProceeds = payload => makePayload(CALCULATE_LOAN_PROCEEDS.value, payload);
function* calculateLoanProceedsSaga(payload) {
  const url = "/calc/calculateLoanProceeds";
  const requestSubKeySaga = subKeySaga(payload.subKey);
  yield requestSubKeySaga(CALCULATE_LOAN_PROCEEDS, url, "POST", payload);
  if (payload.callback) {
    payload.callback();
  }
}

export const DOWNLOAD_LOAN_PROCEEDS = generateDefaultActions("DOWNLOAD_LOAN_PROCEEDS");
export const downloadLoanProceeds = payload => makePayload(DOWNLOAD_LOAN_PROCEEDS.value, payload);
function* downloadLoanProceedsSaga(payload) {
  try {
    yield call(downloadXLSFile, "downloadLoanProceeds", "POST", payload);
  } catch (error) {
    yield put(DOWNLOAD_LOAN_PROCEEDS.createFailure(error));
  }
}

export function* testHarnessSaga() {
  yield takeLatest(GET_TEST_DATA.value, getTestDataSaga);
  yield takeLatest(GET_YIELD_TEST_DATA.value, getYieldTestDataSaga);
  yield takeLatest(GET_AMORT_TEST_DATA.value, getAmortTestDataSaga);
  yield takeLatest(CALCULATE_PREPAYMENT_PENALTY.value, calculatePrepaymentPenaltySaga);
  yield takeLatest(DOWNLOAD_TEST_DATA.value, downloadDataSaga);
  yield takeLatest(DOWNLOAD_AMORT_TEST_DATA.value, downloadAmortizationTestSaga);
  yield takeLatest(DOWNLOAD_PREPAYMENT_PENALTY_TEST_DATA.value, downloadPrepaymentPenaltyTestSaga);
  yield takeLatest(DOWNLOAD_CAP_PRICER_TEST_DATA.value, downloadCapPricerTestDataSaga);
  yield takeLatest(CALCULATE_LOAN_PROCEEDS.value, calculateLoanProceedsSaga);
  yield takeLatest(DOWNLOAD_LOAN_PROCEEDS.value, downloadLoanProceedsSaga);
  yield takeLatest(CALCULATE_REFI_BREAKEVEN.value, calculateRefiBreakEvenSaga);
}
